<script lang="ts" setup>
import { useResponsive } from "@composables"

const { isDesktop } = useResponsive()

const cardList = [
    {
        description:
            "Étude <strong>nationale, gratuite et sécurisée</strong>, menée avec le CSTB*",
        logo: "https://res.cloudinary.com/dcoutwkb1/image/upload/w_120,f_auto,q_auto/v1745852809/icon_handcheck_qczxbs.png",
        alt: "Logo de la main qui fait un check",
        animate: `animate-enter fade-in-10 slide-in-from-t-20 animate-duration-1000`,
    },
    {
        description:
            "Étude visant à <strong>mieux orienter les aides publiques futures</strong>",
        logo: "https://res.cloudinary.com/dcoutwkb1/image/upload/w_120,f_auto,q_auto/v1745852808/icon_abacus_omjdpd.png",
        alt: "Logo de l'aide publique",
        animate: `animate-enter fade-in-10 slide-in-from-b-20 animate-duration-1000`,
    },
    {
        description:
            "Étude des économies d'énergie réelles après rénovation, <strong>sans usage commercial des données</strong>",
        logo: "https://res.cloudinary.com/dcoutwkb1/image/upload/w_120,f_auto,q_auto/v1745852809/icon_lock_wvswft.png",
        alt: "Logo de la protection des données",
        animate: `animate-enter fade-in-10 slide-in-from-t-20 animate-duration-1000`,
    },
]
</script>

<template>
    <div class="what-is-section">
        <h3 class="what-is-section__title">
            Qu'est-ce que
            <img
                src="https://res.cloudinary.com/dcoutwkb1/image/upload/q_auto/v1745851008/logo_black_qzgz7i.svg"
                alt="WattWatchers"
                class="what-is-section__title-logo"
                loading="lazy"
            />
            ?
        </h3>
        <!-- Liste de cartes -->
        <div class="what-is-section__cards">
            <div
                v-for="(card, index) in cardList"
                v-animateonscrollv2="
                    isDesktop && {
                        enterClass: card.animate,
                    }
                "
                :key="index"
                class="what-is-section__card"
            >
                <img
                    :src="card.logo"
                    class="what-is-section__card-image"
                    :alt="card.alt"
                />
                <div class="what-is-section__card-description">
                    <p v-html="card.description"></p>
                </div>
            </div>
        </div>

        <div class="what-is-section__description">
            <img
                src="https://res.cloudinary.com/dcoutwkb1/image/upload/w_120,f_auto,q_auto/v1745852809/icon_light_bulb_ys4k4h.png"
                alt=""
                loading="lazy"
                class="what-is-section__description-image"
            />
            <p class="what-is-section__description-text">
                Le <strong>CSTB*</strong> (Centre Scientifique et Technique du
                Bâtiment) est un établissement public français. Sa mission est
                de soutenir l'innovation et la qualité dans le bâtiment, via la
                recherche, l'expertise, la certification, les essais, et la
                diffusion des connaissances.
            </p>
        </div>
    </div>
</template>

<style scoped>
@import "@styles/breakpoints.css";

.what-is-section {
    display: flex;
    padding: 7.5rem 0rem 5rem 0rem;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 3.5rem;
    align-self: stretch;

    @media (--large-viewport) {
        /** Pour s'assurer de bien commencer au début du block lors du scrollIntoView */
        scroll-margin-top: 5rem;
    }

    @media (--medium-viewport) {
        padding: 0;
        /** Pour s'assurer de bien commencer au début du block lors du scrollIntoView */
        scroll-margin-top: 0;
    }
    .what-is-section__title {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        gap: 0.25rem;

        color: #092754;

        font-size: 2rem;
        font-weight: 600;

        .what-is-section__title-logo {
            width: 15rem;
            height: 1.5rem;
        }
    }

    .what-is-section__description {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        gap: 0.5rem;
        align-self: stretch;
        .what-is-section__description-image {
            width: 2.5rem;
            height: 2.5rem;
            aspect-ratio: 1/1;
        }
        .what-is-section__description-text {
            color: #092754;
            font-weight: 300;
            line-height: 140%;
            strong {
                font-weight: 600;
            }
        }
    }

    .what-is-section__cards {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        align-self: stretch;

        /* Sur mobile */
        @media (--medium-viewport) {
            display: grid;
            grid-template-columns: repeat(3, 17.5rem);
            overflow-y: scroll;
            padding: 0.5rem;
        }
    }

    .what-is-section__card {
        display: flex;
        padding: 3.5rem 1rem;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
        flex: 1 0 0;
        align-self: stretch;

        border-radius: 2rem;
        background: #fff;

        /* Shadow 100 */
        box-shadow: 0px 1px 8px 0px rgba(0, 0, 0, 0.1);

        border: 2px solid transparent;

        transition:
            border-color 0.3s ease-in,
            color 0.3s ease-in;

        /* Sur tablet */
        @media (--large-viewport) {
            gap: 3rem;
        }

        /* Sur mobile */
        @media (--medium-viewport) {
            gap: 1rem;
        }

        &:hover {
            border-radius: 2rem;
            border: 2px solid #1d6ee7;
            background: #fff;

            /* Shadow 300 */
            box-shadow: 0px 1px 24px 0px rgba(0, 0, 0, 0.1);

            p {
                color: #1d6ee7 !important;
            }
        }

        .what-is-section__card-image {
            display: flex;
            width: 2.5rem;
            height: 2.5rem;
            justify-content: center;
            align-items: center;
            aspect-ratio: 1/1;
        }
        .what-is-section__card-description {
            display: flex;
            align-items: center;

            width: 100%;
            max-width: 21rem;
            min-height: 9.5rem;
            p {
                color: #092754;
                text-align: center;
                font-size: 1.5rem;
                font-weight: 400;
                line-height: 160%; /* 2.4rem */

                strong {
                    font-weight: 700;
                }
            }
        }
    }
}
</style>
